import { BillyMark } from "@/components/BillyLogo";
import {
  Printer,
  BookOpen,
  Camera,
  Wallet,
  Receipt as ReceiptIcon,
  PiggyBank,
  Sprout,
  CalendarDays,
  Search,
  ArrowRight,
} from "lucide-react";

/* -----------------------------------------------------------------------
   Billy — Training Manual (exhaustive edition)
   Print-optimised A4 booklet. Uses print CSS to strip nav & tighten margins.
   Voice: casual, tradie-friendly, Billy speaks in first person occasionally.
   Structure: Cover -> Foreword -> TOC -> Chapters -> FAQ -> Troubleshooting
   -> Glossary -> Rhythm calendar -> Closing.
   ----------------------------------------------------------------------- */

const Chapter = ({ num, title, subtitle, icon: Icon, children, testid }) => (
  <section
    className="manual-chapter break-before-page mb-12"
    data-testid={testid}
  >
    <div className="border-b-4 border-black pb-3 mb-6">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 flex items-center gap-2">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        Chapter {num}
      </div>
      <h2 className="font-display font-black text-3xl tracking-tight mt-1">
        {title}
      </h2>
      {subtitle ? (
        <p className="text-neutral-600 italic mt-1 text-[15px]">{subtitle}</p>
      ) : null}
    </div>
    <div className="space-y-4 text-[15px] leading-relaxed">{children}</div>
  </section>
);

const Section = ({ title, children }) => (
  <div className="mb-6 break-inside-avoid">
    <h3 className="font-display font-extrabold text-lg mb-2 border-l-4 border-black pl-3">
      {title}
    </h3>
    <div className="pl-4 space-y-2">{children}</div>
  </div>
);

const Steps = ({ items }) => (
  <ol className="list-decimal ml-6 space-y-1.5 text-[14px]">
    {items.map((s, i) => (
      <li key={i}>{s}</li>
    ))}
  </ol>
);

const Bullets = ({ items }) => (
  <ul className="list-disc ml-6 space-y-1 text-[14px]">
    {items.map((s, i) => (
      <li key={i}>{s}</li>
    ))}
  </ul>
);

const Callout = ({ label, children, tone = "info" }) => {
  const toneCls =
    tone === "warn"
      ? "border-l-4 border-l-amber-500 bg-amber-50"
      : tone === "danger"
        ? "border-l-4 border-l-red-500 bg-red-50"
        : tone === "good"
          ? "border-l-4 border-l-green-600 bg-green-50"
          : tone === "billy"
            ? "border-l-4 border-l-black bg-neutral-100"
            : "border-l-4 border-l-indigo-500 bg-indigo-50";
  return (
    <div className={`p-4 my-3 break-inside-avoid ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-700 mb-1">
        {label}
      </div>
      <div className="text-sm text-neutral-800">{children}</div>
    </div>
  );
};

// Fun "Billy says" pull-quote block
const BillySays = ({ children }) => (
  <div className="my-4 border-2 border-black p-4 bg-white flex items-start gap-3 break-inside-avoid">
    <div className="shrink-0 h-10 w-10 rounded-full bg-black text-white flex items-center justify-center font-display font-black">
      B
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">
        Billy says
      </div>
      <div className="text-[14px] mt-1 italic">{children}</div>
    </div>
  </div>
);

// "What you'll see on screen" — a stylised ASCII/CSS mock of a screen
const ScreenMock = ({ title, children }) => (
  <div className="my-4 break-inside-avoid">
    <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
      What you&apos;ll see
    </div>
    <div className="border-2 border-black rounded-none">
      <div className="border-b-2 border-black bg-black text-white px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold flex items-center justify-between">
        <span>{title}</span>
        <span className="text-neutral-400">Billy</span>
      </div>
      <div className="p-3 bg-white text-[12px] font-mono leading-snug">
        {children}
      </div>
    </div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex items-center justify-between border-b border-dotted border-neutral-300 py-1">
    <span className="text-neutral-600">{k}</span>
    <span className="font-bold">{v}</span>
  </div>
);

// FAQ item
const QA = ({ q, a }) => (
  <div className="break-inside-avoid mb-4">
    <div className="font-display font-extrabold text-[15px]">Q. {q}</div>
    <div className="text-[14px] text-neutral-700 mt-1">A. {a}</div>
  </div>
);

// TOC entry
const TocEntry = ({ n, title, blurb }) => (
  <li className="flex items-baseline gap-4 border-b border-dotted border-neutral-300 pb-2">
    <span className="font-display font-black text-lg mono text-neutral-400 w-8">
      {String(n).padStart(2, "0")}
    </span>
    <div className="flex-1">
      <div className="font-display font-extrabold">{title}</div>
      <div className="text-xs text-neutral-500">{blurb}</div>
    </div>
  </li>
);

const Manual = () => {
  const today = new Date().toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div data-testid="manual-page" className="bg-white text-black min-h-screen">
      {/* Action bar — hidden on print */}
      <div className="print:hidden border-b-2 border-black bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 inline-flex items-center gap-2">
            <BookOpen className="h-4 w-4" /> Training Manual
          </div>
          <button
            data-testid="manual-print-btn"
            onClick={() => window.print()}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
          >
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      {/* Manual body */}
      <div className="max-w-4xl mx-auto px-6 py-10 print:py-0 print:px-0">
        {/* COVER PAGE */}
        <section
          className="manual-cover break-after-page mb-10 min-h-[90vh] flex flex-col justify-between print:min-h-screen"
          data-testid="manual-cover"
        >
          <div>
            <BillyMark className="h-16 w-16" />
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mt-8">
              Training Manual · Edition 2 · Exhaustive
            </div>
            <h1 className="font-display font-black text-6xl md:text-7xl tracking-tighter leading-[0.95] mt-4">
              Billy.
              <br />
              <span className="text-neutral-400">The book.</span>
            </h1>
            <p className="text-xl text-neutral-700 mt-6 max-w-xl">
              Every button, every tab, every trick — written for tradies, by
              Billy. If you can read a footy scorecard, you can run this app.
            </p>
          </div>
          <div className="border-t-2 border-black pt-4 flex items-end justify-between">
            <div>
              <div className="font-display font-extrabold text-lg">
                The Outdoor Blind Company
              </div>
              <div className="text-xs text-neutral-500 uppercase tracking-widest font-bold">
                Craig — Owner
              </div>
            </div>
            <div className="text-right text-xs text-neutral-500 uppercase tracking-widest font-bold">
              Printed {today}
            </div>
          </div>
        </section>

        {/* FOREWORD */}
        <section className="break-after-page mb-10" data-testid="manual-foreword">
          <div className="border-b-4 border-black pb-3 mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500">
              Foreword
            </div>
            <h2 className="font-display font-black text-3xl mt-1">
              G&apos;day, I&apos;m Billy.
            </h2>
          </div>
          <div className="space-y-4 text-[15px] leading-relaxed">
            <p>
              I&apos;m the little app that sits in your pocket and does the
              paperwork you keep meaning to do. I read your bills, chase your
              overdue clients, split your cash into the right bank accounts,
              and remind you when the ATO is about to come knocking.
            </p>
            <p>
              I&apos;m not accounting software. I&apos;m not going to make you
              a general ledger. What I&apos;ll do is stop money slipping
              through cracks and give you a fair-dinkum picture of what&apos;s
              going in, what&apos;s going out, and what&apos;s left to actually
              live on.
            </p>
            <p>
              This book is everything I can do — chapter by chapter, button by
              button — plus a big FAQ at the back for when the day goes sideways.
            </p>
            <BillySays>
              Read the first 3 chapters, then keep me on your bench for
              reference. Nobody remembers a manual on the first read. Just
              know where to find it.
            </BillySays>
          </div>
        </section>

        {/* TABLE OF CONTENTS */}
        <section
          className="manual-toc break-after-page mb-10"
          data-testid="manual-toc"
        >
          <div className="border-b-4 border-black pb-3 mb-6">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500">
              Contents
            </div>
            <h2 className="font-display font-black text-3xl mt-1">
              What&apos;s in this book
            </h2>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-2 mb-2">
            Part I · Getting Started
          </div>
          <ol className="space-y-2 text-[15px]">
            <TocEntry n={1} title="G'day, I'm Billy" blurb="What I do & what I don't" />
            <TocEntry n={2} title="Logging in the first time" blurb="URL, credentials, home-screen tricks" />
            <TocEntry n={3} title="The Billy Way — SNAP · ALLOCATE · CHASE" blurb="The whole system in three moves" />
          </ol>

          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-4 mb-2">
            Part II · The Daily Tools
          </div>
          <ol className="space-y-2 text-[15px]" start={4}>
            <TocEntry n={4} title="SNAP — the magic button" blurb="OCR, Smart Capture, tips" />
            <TocEntry n={5} title="Bills — money going out" blurb="Suppliers, payments, filters, series" />
            <TocEntry n={6} title="Receipts — for BAS & tax" blurb="Categories & quarterly BAS export" />
            <TocEntry n={7} title="Income — money coming in" blurb="Deposits, service calls, quote splits" />
            <TocEntry n={8} title="Overheads — the fixed nut" blurb="Rent, wages, super, insurance" />
          </ol>

          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-4 mb-2">
            Part III · The Weekly Rhythm
          </div>
          <ol className="space-y-2 text-[15px]" start={9}>
            <TocEntry n={9} title="Weekly — your morning-coffee tab" blurb="KPIs, chart, print buttons" />
            <TocEntry n={10} title="Allocate — envelope budgeting" blurb="How every dollar gets a job" />
            <TocEntry n={11} title="Statements & Print pack" blurb="Bank, accountant, ATO paperwork" />
          </ol>

          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-4 mb-2">
            Part IV · The Growth Toolkit
          </div>
          <ol className="space-y-2 text-[15px]" start={12}>
            <TocEntry n={12} title="Grow — 28 revenue plays" blurb="Ideas that make you money" />
            <TocEntry n={13} title="Jobs — did that job actually pay?" blurb="Per-job profit tracking" />
            <TocEntry n={14} title="3-Month Forecast" blurb="The crystal-ball view" />
          </ol>

          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-4 mb-2">
            Part V · The Rest of the Toolbox
          </div>
          <ol className="space-y-2 text-[15px]" start={15}>
            <TocEntry n={15} title="Calendar view" blurb="Month-at-a-glance dots" />
            <TocEntry n={16} title="P&L printout" blurb="Profit / loss for accountants" />
            <TocEntry n={17} title="History / audit log" blurb="What changed, who did it" />
            <TocEntry n={18} title="Cmd+K universal search" blurb="Find anything in 2 seconds" />
            <TocEntry n={19} title="Series & recurring bills" blurb="Payment plans on autopilot" />
            <TocEntry n={20} title="Public pay-links" blurb="One-tap payments for customers" />
            <TocEntry n={21} title="Bank statement OCR" blurb="Ingest a whole month at once" />
          </ol>

          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mt-4 mb-2">
            Part VI · Reference
          </div>
          <ol className="space-y-2 text-[15px]" start={22}>
            <TocEntry n={22} title="Frequently Asked Questions" blurb="30+ answers to real questions" />
            <TocEntry n={23} title="Troubleshooting" blurb="When something breaks" />
            <TocEntry n={24} title="Glossary" blurb="Every Billy term in plain English" />
            <TocEntry n={25} title="Your yearly rhythm" blurb="Daily / weekly / quarterly / EOFY checklist" />
          </ol>
        </section>

        {/* ============== PART I ============== */}
        <div className="break-before-page">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">
            Part I
          </div>
          <h1 className="font-display font-black text-5xl leading-none mb-10">
            Getting Started
          </h1>
        </div>

        {/* CH 01 */}
        <Chapter
          num="01"
          title="G'day, I'm Billy"
          subtitle="What I do, what I don't, and why you'd bother using me."
          testid="ch-01"
        >
          <p>
            Billy is a cashflow and bill reminder app built for tradies and
            small business owners. I replace the pile of receipts on the
            dashboard of your ute, the missed invoices in your inbox, and the
            spreadsheet you promised your accountant last week.
          </p>

          <Section title="What I do">
            <Bullets
              items={[
                "Read any bill / receipt / invoice / quote / bank statement from a photo (that's the AI OCR I use)",
                "Track money owed OUT (Bills) and money owed IN (Income)",
                "Record every payment against the right invoice — forever",
                "Print bank-ready statements, monthly reports, 3-month forecasts, BAS packs and full P&L",
                "Split your incoming cash into the right envelopes — wages, super, GST, rent — in the right priority order",
                "Chase overdue clients via public pay-links (one tap → paid)",
                "Give you 28 curated revenue plays for outdoor-blind and small-trade retail",
              ]}
            />
          </Section>

          <Section title="What I am NOT">
            <Bullets
              items={[
                "Not accounting software. No double-entry ledger, no journals.",
                "Not a replacement for MYOB / Xero if your accountant demands them — but I'll feed them everything they need in a CSV.",
                "Not a substitute for actually talking to overdue customers — but I make it faster.",
                "Not a bank. I never touch your money. I tell you what to move, you make the transfers.",
              ]}
            />
          </Section>

          <BillySays>
            Think of me as your paperwork mate. I don&apos;t know how to hang a
            blind, but I can tell you which job made $2,400 and which one lost
            you $300.
          </BillySays>
        </Chapter>

        {/* CH 02 */}
        <Chapter
          num="02"
          title="Logging in the first time"
          subtitle="Get me on your phone home-screen in 90 seconds."
          testid="ch-02"
        >
          <Section title="Where to find me">
            <p>
              Billy lives at your Emergent-hosted URL (bookmark it). On your
              phone, open it in Safari (iPhone) or Chrome (Android), then use
              the browser menu → &quot;Add to Home Screen&quot;. You&apos;ll
              get a Billy icon just like a real app.
            </p>
          </Section>

          <Section title="Your login">
            <Bullets
              items={[
                "Email: craig@theoutdoorblindcompany.com",
                "Password: Holden355 (capital H, no spaces)",
                "Tick 'remember me' — I'll keep you signed in on that phone for 30 days.",
              ]}
            />
          </Section>

          <ScreenMock title="Login screen">
            <div className="space-y-1">
              <div className="border border-neutral-400 px-2 py-1">Email</div>
              <div className="border border-neutral-400 px-2 py-1">Password</div>
              <div className="bg-black text-white text-center py-1 mt-2">
                Sign in
              </div>
            </div>
          </ScreenMock>

          <Callout label="Security" tone="warn">
            Never share the password by email or text. If a staff member needs
            access, ask for a separate login to be created — don&apos;t hand
            out yours. Every action gets logged to the History tab, tied to
            whoever&apos;s signed in.
          </Callout>

          <Section title="Signing out">
            <p>
              Top-right of every page — the small &quot;Sign out&quot; button.
              Use it if you&apos;re on a shared computer or you&apos;ve just
              given the ute to your apprentice.
            </p>
          </Section>
        </Chapter>

        {/* CH 03 */}
        <Chapter
          num="03"
          title="The Billy Way — SNAP · ALLOCATE · CHASE"
          subtitle="If you do these three moves every day, I run the business for you."
          testid="ch-03"
        >
          <p className="text-lg font-display font-bold">
            Three verbs. Three habits. Everything else is optional.
          </p>

          <Section title="1. SNAP">
            <p>
              Any piece of paper that lands in your hand — a supplier invoice,
              a petrol receipt, a customer quote, a bank statement — press the
              black <span className="font-bold">SNAP</span> button top right,
              take a photo, done.
            </p>
            <p className="text-sm text-neutral-600">
              My AI reads it, works out what type of document it is, files it
              in the right tab, and pre-fills every field. You just review and
              hit save. If I get the type wrong, tap the correct type at the
              top of the review page.
            </p>
          </Section>

          <Section title="2. ALLOCATE">
            <p>
              Every time a customer pays you, mark that Income as{" "}
              <span className="font-bold">received</span>. Then open the{" "}
              <span className="font-bold">Allocate</span> tab — I tell you
              exactly how to divide that cash across your bank accounts
              (wages, super, BAS, rent, buffer) in priority order.
            </p>
          </Section>

          <Section title="3. CHASE">
            <p>
              At the end of each day, check the{" "}
              <span className="font-bold">Weekly</span> tab. Any overdue
              customers? Open the bill → copy the public pay-link → SMS them.
              Done in 30 seconds.
            </p>
          </Section>

          <Callout label="The rule" tone="good">
            SNAP → ALLOCATE → CHASE. That&apos;s the whole system. Everything
            else is bonus.
          </Callout>
        </Chapter>

        {/* ============== PART II ============== */}
        <div className="break-before-page">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">
            Part II
          </div>
          <h1 className="font-display font-black text-5xl leading-none mb-10">
            The Daily Tools
          </h1>
        </div>

        {/* CH 04 - SNAP deep dive */}
        <Chapter
          num="04"
          title="SNAP — the magic button"
          subtitle="How the camera + AI work, and how to get the most out of them."
          icon={Camera}
          testid="ch-04"
        >
          <Section title="What SNAP does">
            <p>
              SNAP is the shortcut to capture anything on paper or PDF. Under
              the hood it uses GPT-Vision (an AI trained on millions of
              documents) to read the image and extract structured fields —
              amounts, dates, vendors, ABNs, invoice numbers, BPAY/PayID/BSB
              details, quote line items — you name it.
            </p>
          </Section>

          <Section title="How to SNAP (step by step)">
            <Steps
              items={[
                "Press the black SNAP button top right (visible on every page).",
                "Choose the source: Camera (take a fresh photo) or Upload (from your gallery / PDF).",
                "Hold the phone steady — flat, no shadow, whole document in frame. 3 seconds of care here saves 30 seconds of manual typing later.",
                "Wait 3–8 seconds while I process. You'll see a spinner.",
                "I'll auto-classify the document as one of: BILL, RECEIPT, QUOTE, BANK STATEMENT, INCOME. If I got it wrong, tap the correct type at the top.",
                "Review the extracted fields. Correct anything that's off (rare, but always double-check the amount).",
                "Hit Save. The record lands in the correct tab.",
              ]}
            />
          </Section>

          <ScreenMock title="SNAP · classification result">
            <KV k="Detected type" v="BILL" />
            <KV k="Vendor" v="JJ's Waste" />
            <KV k="Invoice #" v="INV-284712" />
            <KV k="Amount" v="$174.20" />
            <KV k="GST" v="$15.84" />
            <KV k="Due date" v="27 Feb 2026" />
            <KV k="BPAY" v="Biller 12345 · Ref 998877" />
            <div className="bg-black text-white text-center py-1 mt-2">
              Save bill
            </div>
          </ScreenMock>

          <Section title="Types I can read">
            <Bullets
              items={[
                "Supplier invoice (Optus, JJ's Waste, Nolans, fabric supplier)",
                "Petrol / Bunnings / hardware receipts",
                "Customer quotes (I offer to auto-generate the 50/30/20 deposit incomes)",
                "Bank statements (multi-line — I walk you through each transaction)",
                "Handwritten dockets (mostly — legibility matters)",
              ]}
            />
          </Section>

          <Section title="Tips for perfect OCR">
            <Bullets
              items={[
                "Flat surface, natural light, no shadow from your hand.",
                "Whole document visible — including totals at the bottom.",
                "Portrait orientation for receipts, landscape for A4 bills.",
                "PDFs work great — just Upload rather than photograph.",
                "Blurry? Retake. I'll happily read a good photo but I can't magic-fix a bad one.",
              ]}
            />
          </Section>

          <BillySays>
            If the amount looks weird, check the photo. 99% of the time
            it&apos;s a blurry decimal point, not the AI.
          </BillySays>
        </Chapter>

        {/* CH 05 — Bills */}
        <Chapter
          num="05"
          title="Bills — money going out"
          subtitle="Every invoice you owe, in one place, chased automatically."
          icon={Wallet}
          testid="ch-05"
        >
          <Section title="What lives here">
            <p>
              Every invoice you&apos;ve <span className="font-bold">received</span>{" "}
              from a supplier: Optus, JJ&apos;s Waste, Nolans (court plan),
              fabric suppliers, subcontractors, etc. If someone sent you a
              bill, it goes here.
            </p>
            <p className="text-sm text-neutral-600 italic">
              Not to be confused with Overheads (Ch 08), which are the fixed
              weekly nuts like rent and wages.
            </p>
          </Section>

          <Section title="Adding a bill (2 ways)">
            <Steps
              items={[
                "SNAP method (recommended): Press SNAP → take a photo → I classify it as BILL → review → save.",
                "Manual method: Bills tab → 'New Bill' → fill in vendor, amount, due date, invoice #, notes → save.",
                "Optional: attach a job tag like SMITH-123 so Jobs tab groups it later.",
                "Optional: assign to a bank account so Allocate knows which envelope to fund.",
              ]}
            />
          </Section>

          <Section title="Recording a payment">
            <Steps
              items={[
                "Tap any bill to open its detail page.",
                "Enter the amount you paid + the date and hit 'Record payment'.",
                "I update the remaining balance and show part-paid vs paid.",
                "Payment history is kept forever — useful when a supplier claims you didn't pay.",
                "For split payments (e.g. paid $500 last week and $300 today), just record two entries.",
              ]}
            />
          </Section>

          <ScreenMock title="Bill detail · payment recorded">
            <KV k="JJ's Waste · INV-284712" v="" />
            <KV k="Original amount" v="$174.20" />
            <KV k="Payments recorded" v="$174.20 · 24 Feb 2026" />
            <KV k="Remaining balance" v="$0.00" />
            <KV k="Status" v="PAID" />
          </ScreenMock>

          <Section title="Filters">
            <p>Bills list can be filtered by:</p>
            <Bullets
              items={[
                "overdue — past their due date, still unpaid",
                "due soon — due in next 7 days",
                "unpaid — everything owed",
                "part-paid — you've paid some but not all",
                "paid — closed out (kept for records)",
                "emailed — you've sent the pay-link via email",
                "texted — you've sent the pay-link via SMS",
              ]}
            />
          </Section>

          <Section title="Recurring bills & payment plans">
            <p>
              For anything that repeats — like your Nolans court instalments,
              or a monthly Optus bill — set up a{" "}
              <span className="font-bold">series</span>. I generate all future
              instalments automatically. You can:
            </p>
            <Bullets
              items={[
                "Edit the whole series in one hit from the purple series panel on any bill in the group.",
                "Cancel just the unpaid future ones (leaves paid ones untouched).",
                "Change frequency (weekly → monthly, etc.).",
                "Reassign to a different bank account.",
              ]}
            />
          </Section>

          <Callout label="Gotcha" tone="warn">
            If you record a payment on the WRONG bill, use the History tab to
            find it and undo. Never delete the bill — that loses the audit
            trail.
          </Callout>
        </Chapter>

        {/* CH 06 — Receipts */}
        <Chapter
          num="06"
          title="Receipts — for BAS & tax"
          subtitle="Every servo, Bunnings and cafe receipt = money in your pocket at tax time."
          icon={ReceiptIcon}
          testid="ch-06"
        >
          <Section title="Why bother recording receipts">
            <Bullets
              items={[
                "Every GST-paid receipt = a GST credit you can claim back on BAS",
                "Every business expense = a tax deduction come EOFY",
                "Miss one = lose real money (usually $5-15 per receipt for a tradie)",
              ]}
            />
          </Section>

          <Section title="How to add one">
            <Steps
              items={[
                "Press SNAP → photo of the receipt (servo, Bunnings, cafe, tools, materials).",
                "I extract vendor, amount, GST, category.",
                "Fix the category if I guessed wrong: Materials / Fuel / Tools / Meals / Vehicle / Other.",
                "Optional: tag with a job (e.g. SMITH-123) so this receipt lands under that job's costs in the Jobs tab.",
                "Save. Never lose it again.",
              ]}
            />
          </Section>

          <ScreenMock title="Receipt · extraction result">
            <KV k="Vendor" v="Bunnings Warehouse" />
            <KV k="Amount (inc. GST)" v="$127.40" />
            <KV k="GST" v="$11.58" />
            <KV k="Category" v="Materials" />
            <KV k="Job tag" v="SMITH-123 (optional)" />
          </ScreenMock>

          <Section title="Exporting BAS quarterly">
            <Steps
              items={[
                "Receipts tab → 'Export' button top right.",
                "Choose the quarter (Jul-Sep, Oct-Dec, Jan-Mar, Apr-Jun).",
                "Download the CSV (for your accountant) and the print-ready PDF (for your records).",
                "Attach to your BAS submission or email straight to the accountant.",
              ]}
            />
          </Section>

          <BillySays>
            Do this the day you fill up, buy tools or grab a coffee with a
            client. If you leave it a week, half the receipts vanish into the
            ute upholstery.
          </BillySays>
        </Chapter>

        {/* CH 07 — Income */}
        <Chapter
          num="07"
          title="Income — money coming in"
          subtitle="Every deposit, service call and quote — logged so I can allocate it."
          testid="ch-07"
        >
          <Section title="Categories">
            <Bullets
              items={[
                "Install deposit 50% — up-front on order",
                "Install deposit 30% — pre-install site check",
                "Install deposit 20% — on completion",
                "Service call — call-out to fix something on-site",
                "Factory repair — bench repair back at the workshop",
                "Other — anything that doesn't fit the above",
              ]}
            />
          </Section>

          <Section title="Adding income the fast way (quote → 3 auto-incomes)">
            <p>
              When you win a job: SNAP the quote you sent. I detect it&apos;s
              a QUOTE and ask if you want to auto-generate the 3 install-stage
              incomes (50/30/20). Say yes — three future income entries appear,
              correctly dated to your install timeline. You&apos;ve just
              scheduled all your revenue for that job in 10 seconds.
            </p>
          </Section>

          <Section title="Adding income manually">
            <Steps
              items={[
                "Income tab → 'New Income'.",
                "Client name, amount, category, expected date, bank account it'll land in.",
                "Optional job tag (SMITH-123) so it groups with the bill costs in Jobs tab.",
                "Save. It shows as EXPECTED (not yet received).",
              ]}
            />
          </Section>

          <Section title="Marking received (the critical step)">
            <p>
              When a customer pays, open the Income entry and tick{" "}
              <span className="font-bold">Received</span>. That&apos;s the
              signal I need to trigger the Allocate split — do NOT skip this
              step. If cash lands in the bank but Billy doesn&apos;t know,
              your envelopes stay empty and the ATO doesn&apos;t get funded.
            </p>
          </Section>

          <Callout label="Rule of thumb" tone="good">
            Cash in bank → same-day mark received in Billy. Make it a habit
            like locking the ute at night.
          </Callout>
        </Chapter>

        {/* CH 08 — Overheads */}
        <Chapter
          num="08"
          title="Overheads — the fixed nut"
          subtitle="The stuff you pay every week/month like clockwork. Kept separate from bills so nothing gets buried."
          testid="ch-08"
        >
          <Section title="What goes here vs Bills">
            <p>
              <span className="font-bold">Overheads</span> = same amount, same
              day, every week/month: rent, wages, super, insurance,
              subscriptions, vehicle payments.
            </p>
            <p>
              <span className="font-bold">Bills</span> = one-off supplier
              invoices that vary each time.
            </p>
            <p>
              Keeping them apart stops the Bills tab getting cluttered, and
              lets me count them properly in the Allocate priority order.
            </p>
          </Section>

          <Section title="Setting one up">
            <Steps
              items={[
                "Overheads tab → 'Add overhead'.",
                "Name (e.g. '21 Eugine Terrace rent', 'Steve Brailee wages').",
                "Category: Rent / Wages / Super / Insurance / Vehicle / Subscription / Other.",
                "Amount and frequency (weekly / fortnightly / monthly / quarterly / yearly).",
                "Due day — day of week for weekly, day of month for monthly, etc.",
                "Which bank account it comes from.",
                "Notes (optional) — e.g. 'pays into landlord account BSB 083-004 12345678'.",
                "Save.",
              ]}
            />
          </Section>

          <ScreenMock title="Overheads card">
            <KV k="21 Eugine Terrace rent" v="$450 / week" />
            <KV k="Due" v="Every Monday" />
            <KV k="From account" v="Operating account" />
            <KV k="Last paid" v="19 Feb 2026" />
            <div className="bg-black text-white text-center py-1 mt-2">
              Paid $450
            </div>
          </ScreenMock>

          <Section title="Marking as paid each week">
            <p>
              When you pay it: press the black &quot;Paid $X&quot; button on
              the card. History is recorded. Weekly totals update. Allocate
              tab dynamically re-balances.
            </p>
          </Section>

          <Section title="Editing an overhead">
            <p>
              Pencil icon on any overhead card → change amount, day, notes,
              account. Save. All future weeks pick up the new number
              automatically.
            </p>
          </Section>

          <BillySays>
            Enter your overheads ONCE, keep them tidy forever. They&apos;re
            the backbone of your Allocate calculations.
          </BillySays>
        </Chapter>

        {/* ============== PART III ============== */}
        <div className="break-before-page">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">
            Part III
          </div>
          <h1 className="font-display font-black text-5xl leading-none mb-10">
            The Weekly Rhythm
          </h1>
        </div>

        {/* CH 09 — Weekly */}
        <Chapter
          num="09"
          title="Weekly — your morning-coffee tab"
          subtitle="Open this first every day. It's the pulse of your business."
          icon={CalendarDays}
          testid="ch-09"
        >
          <Section title="5 KPI tiles at the top">
            <Bullets
              items={[
                "Outgoing — bills due this week",
                "Overdue — bills that were due before this week and still unpaid",
                "Expected In — income booked but not received yet",
                "Received — money actually in the bank this week",
                "Still Needed — what needs to land before Sunday to hit your profit goal",
              ]}
            />
          </Section>

          <ScreenMock title="Weekly · KPI tiles">
            <div className="grid grid-cols-5 gap-1">
              <div className="border p-1 text-center"><div className="text-[9px]">OUT</div><div className="font-bold">$3.2k</div></div>
              <div className="border p-1 text-center"><div className="text-[9px]">OVERDUE</div><div className="font-bold text-red-600">$1.1k</div></div>
              <div className="border p-1 text-center"><div className="text-[9px]">EXP IN</div><div className="font-bold">$8.4k</div></div>
              <div className="border p-1 text-center"><div className="text-[9px]">RECEIVED</div><div className="font-bold text-green-700">$4.5k</div></div>
              <div className="border p-1 text-center"><div className="text-[9px]">STILL NEEDED</div><div className="font-bold">$1.9k</div></div>
            </div>
          </ScreenMock>

          <Section title="The stacked chart">
            <p>
              Ins vs Outs across the last few weeks. The blue/teal stack
              shows your <span className="font-bold">Expected income</span>{" "}
              broken down by category — install deposits, service calls,
              factory repairs. Important: that stack is{" "}
              <span className="font-bold">not overdue</span> — it&apos;s your
              normal payment process timing.
            </p>
          </Section>

          <Section title="Print buttons">
            <Bullets
              items={[
                "Weekly — print this week + next week (great for Monday morning huddles)",
                "Monthly — this whole month, with tips",
                "3-Mo Forecast — past 3 months + next 3, running balance (banks love this for loan applications)",
                "Custom — any date range you want",
              ]}
            />
          </Section>

          <Section title="What to check every morning (2 min)">
            <Steps
              items={[
                "Overdue tile — anyone red? Copy their pay-link and SMS them.",
                "Outgoing tile — is there anything I haven't paid that's due today?",
                "Expected In tile — any income due today? Watch for the deposit landing.",
                "Still Needed tile — if positive, ring a warm lead or push a chase harder.",
              ]}
            />
          </Section>
        </Chapter>

        {/* CH 10 — Allocate */}
        <Chapter
          num="10"
          title="Allocate — envelope budgeting"
          subtitle="Every dollar gets a job — in priority order — so the ATO never surprises you."
          icon={PiggyBank}
          testid="ch-10"
        >
          <p>
            This is where I earn my keep. Every dollar that lands gets
            allocated to the right bank account, and I show you the maths so
            you can trust it.
          </p>

          <Section title="The 8 default buckets (edit anytime)">
            <ol className="list-decimal ml-6 space-y-1 text-[14px]">
              <li>Wages — take-home for you and any staff</li>
              <li>Staff Super — 11.5% of wages (legally required)</li>
              <li>Court / plan payments — auto-detects Nolans etc.</li>
              <li>Rent — fixed weekly amount</li>
              <li>Bills due this week — auto from Bills tab</li>
              <li>BAS / GST set-aside — 15% of income by default</li>
              <li>Tax debt (ATO) — fixed weekly amount</li>
              <li>Buffer / Profit — whatever&apos;s left over</li>
            </ol>
          </Section>

          <ScreenMock title="Allocate · this week">
            <KV k="Received this week" v="$4,500.00" />
            <div className="mt-2 border-t border-dotted border-neutral-400 pt-2 space-y-1">
              <KV k="1. Wages (Craig + Steve)" v="$2,100" />
              <KV k="2. Super (11.5%)" v="$241.50" />
              <KV k="3. Nolans court plan" v="$385" />
              <KV k="4. Rent" v="$450" />
              <KV k="5. Bills this week" v="$620" />
              <KV k="6. GST set-aside" v="$450" />
              <KV k="7. ATO debt" v="$150" />
              <KV k="8. Buffer" v="$103.50" />
            </div>
          </ScreenMock>

          <Section title="How the maths works">
            <p>
              I fund each bucket <span className="font-bold">in priority order</span>.
              Wages and Super go first — they&apos;re legally protected. Then
              court/plan payments, then rent, then bills, then tax, then
              buffer.
            </p>
            <p>
              If you don&apos;t bring in enough this week, lower-priority
              buckets get part-funded and I show exactly what&apos;s short —
              so you know what to chase.
            </p>
          </Section>

          <Section title="Billy Insight">
            <p>
              Under the buckets you&apos;ll see 4–6 paragraphs explaining
              exactly why each dollar went where and what to do about
              shortfalls. Read it every week — it&apos;s better than a paid
              advisor because it&apos;s built on YOUR real numbers.
            </p>
          </Section>

          <Section title="Chaseable money">
            <p>
              A separate green panel lists every overdue receivable — money
              customers still owe you. Copy the pay-link from each bill and
              send an SMS. This is the fastest way to fix a bad week — often
              a $200 SMS turns into a $2,000 transfer within an hour.
            </p>
          </Section>

          <Callout label="The mindset shift" tone="billy">
            You&apos;re not &quot;keeping&quot; the money in your main
            account. Every dollar has a job — even if that job is
            &quot;waiting for the ATO&quot;. Move it out of sight so it
            can&apos;t be spent by accident.
          </Callout>
        </Chapter>

        {/* CH 11 — Statement & Print */}
        <Chapter
          num="11"
          title="Statements & the Print Pack"
          subtitle="Bank-ready, accountant-ready, ATO-ready — one click."
          testid="ch-11"
        >
          <Section title="What print outputs are available">
            <Bullets
              items={[
                "Weekly Statement — this week + next week, all bills/income/overheads",
                "Monthly Statement — full month with weekly breakdowns",
                "3-Month Forecast — past 3 months + next 3, running bank balance line",
                "Custom Range — any two dates you pick",
                "BAS Pack — quarterly GST paid / GST collected export",
                "P&L — profit and loss for any period",
                "Jobs Report — per-job profit summary",
              ]}
            />
          </Section>

          <Section title="How to print / save PDF">
            <Steps
              items={[
                "Weekly tab → pick the print button you want (Weekly / Monthly / Forecast / Custom).",
                "A print-optimised page opens in a new tab.",
                "Ctrl+P (or Cmd+P) → 'Save as PDF' → done.",
                "Optionally email the PDF straight to your accountant or bank.",
              ]}
            />
          </Section>

          <BillySays>
            When your bank asks for &quot;3 months of trading data&quot; for a
            loan — print the 3-Month Forecast. It shows what happened AND what&apos;s
            coming. They love it.
          </BillySays>
        </Chapter>

        {/* ============== PART IV ============== */}
        <div className="break-before-page">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">
            Part IV
          </div>
          <h1 className="font-display font-black text-5xl leading-none mb-10">
            The Growth Toolkit
          </h1>
        </div>

        {/* CH 12 — Grow */}
        <Chapter
          num="12"
          title="Grow — 28 revenue plays"
          subtitle="Ideas curated for outdoor blinds and small-trade retail. Pick one a week."
          icon={Sprout}
          testid="ch-12"
        >
          <p>
            28 curated ideas to make more money. Read once a month, pick ONE
            to try. Don&apos;t try 5 at once — that&apos;s how nothing gets
            done.
          </p>

          <Section title="Categories">
            <Bullets
              items={[
                "Quick Wins — do this week, 30 min setup",
                "Recurring Revenue — annual service contracts etc.",
                "Cross-Sell / Upsell — smart-home, motorisation, insect screens",
                "New B2B Markets — cafes, body corporate, insurance repair panel",
                "New Products — Ziptrak, louvred roofs, shutters",
                "Seasonal Plays — storm prep, gift vouchers",
                "Marketing — referrals, Facebook groups",
                "Cashflow — bigger deposits, pay-links, supplier terms",
              ]}
            />
          </Section>

          <Section title="How each play card works">
            <p>Every idea has:</p>
            <Bullets
              items={[
                "The pitch — one sentence explanation",
                "Est. revenue impact — realistic $ per month",
                "Effort — Low / Medium / High",
                "First step — the exact action to take tomorrow",
                "Metric to track — how you'll know it worked",
              ]}
            />
          </Section>

          <Callout label="The rule" tone="good">
            Pick ONE quick win per week. Show up. Do the work. Measure the
            result in Billy&apos;s numbers a month later.
          </Callout>
        </Chapter>

        {/* CH 13 — Jobs */}
        <Chapter
          num="13"
          title="Jobs — did that job actually pay?"
          subtitle="Per-job profit tracking so you stop losing money on cheap quotes."
          testid="ch-13"
        >
          <Section title="How it works">
            <p>
              Tag any bill, receipt, or income with a Job ID (e.g.{" "}
              <span className="mono">SMITH-123</span> or{" "}
              <span className="mono">42-COLLINS-ST</span>) and the Jobs tab
              aggregates the profit of each job automatically. You&apos;ll see
              exactly which jobs made money and which lost you your shirt.
            </p>
          </Section>

          <Section title="How to tag things">
            <Steps
              items={[
                "On any bill / receipt / income detail page, find the 'Job tag' field.",
                "Type the job ID (or pick from recent ones).",
                "Save.",
                "The Jobs tab groups everything by tag automatically.",
              ]}
            />
          </Section>

          <ScreenMock title="Jobs tab · SMITH-123">
            <KV k="Job" v="SMITH-123 · 42 Collins St" />
            <KV k="Income received" v="$4,800" />
            <KV k="Materials (bills + receipts)" v="$1,340" />
            <KV k="Wages allocated" v="$720" />
            <KV k="Gross profit" v="$2,740" />
            <KV k="Margin" v="57%" />
          </ScreenMock>

          <BillySays>
            After 5–10 jobs you&apos;ll see patterns. Some clients pay well.
            Some products earn thin. Use it to quote smarter next time.
          </BillySays>
        </Chapter>

        {/* CH 14 — Forecast */}
        <Chapter
          num="14"
          title="3-Month Forecast — the crystal ball"
          subtitle="Past 3 months + next 3 months on a single running-balance chart."
          testid="ch-14"
        >
          <Section title="What it shows">
            <p>
              A single line: your projected bank balance over the next 12
              weeks based on booked income, known bills, recurring overheads,
              and allocate priorities. If the line dips below zero — you have
              a cashflow crunch coming and need to chase / delay something.
            </p>
          </Section>

          <Section title="How to use it">
            <Steps
              items={[
                "Weekly tab → '3-Mo Forecast' print button.",
                "Look at the running balance line.",
                "Note any weeks where it dips below your minimum-buffer threshold.",
                "For each dip: chase an overdue receivable that lands in that week, or delay a discretionary bill.",
                "Print and share with your accountant / bank when applying for finance.",
              ]}
            />
          </Section>
        </Chapter>

        {/* ============== PART V ============== */}
        <div className="break-before-page">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">
            Part V
          </div>
          <h1 className="font-display font-black text-5xl leading-none mb-10">
            The Rest of the Toolbox
          </h1>
        </div>

        {/* CH 15 — Calendar */}
        <Chapter num="15" title="Calendar view" testid="ch-15">
          <p>
            Month-view of every bill (red dot), income (green dot), receipt
            (blue dot). Click any dot to jump straight to the record.
          </p>
          <Section title="How to use">
            <Steps
              items={[
                "Calendar tab (top nav).",
                "Scroll through months with the arrows.",
                "Tap a coloured dot to open the record.",
                "Use filters top-right to show only bills, only income, only receipts.",
              ]}
            />
          </Section>
        </Chapter>

        {/* CH 16 — P&L */}
        <Chapter num="16" title="P&L printout" testid="ch-16">
          <p>
            Profit and loss for any quarter, month, or full financial year.
            One button print for the accountant.
          </p>
          <Section title="How to use">
            <Steps
              items={[
                "P&L tab (top nav).",
                "Pick a range: This Quarter / Last Quarter / FY-to-date / Custom.",
                "Review income, expenses, GST, net profit.",
                "Print → email to accountant.",
              ]}
            />
          </Section>
        </Chapter>

        {/* CH 17 — History */}
        <Chapter num="17" title="History / audit log" testid="ch-17">
          <p>
            Every action in Billy is logged forever — creates, updates,
            payments, deletes. Useful when you can&apos;t remember what
            changed, or when a staff member says &quot;I didn&apos;t touch
            that&quot;.
          </p>
          <Section title="How to use">
            <Steps
              items={[
                "History tab (top nav).",
                "Scroll — most recent action first.",
                "Filter by record type, user, or date.",
                "Click any log entry to jump to the record.",
              ]}
            />
          </Section>
          <BillySays>
            The audit log is your friend during arguments. &quot;Nope, it was
            you at 3:42pm on Tuesday.&quot;
          </BillySays>
        </Chapter>

        {/* CH 18 — Cmd+K */}
        <Chapter
          num="18"
          title="Cmd+K universal search"
          icon={Search}
          testid="ch-18"
        >
          <p>
            Press <span className="mono font-bold">Ctrl+K</span> (Windows) or{" "}
            <span className="mono font-bold">Cmd+K</span> (Mac) anywhere in
            Billy. Type any company, invoice number, client name, job tag,
            amount or note. Instant results, jump straight to the record.
          </p>
          <Section title="Examples">
            <Bullets
              items={[
                "Type 'JJ' → all JJ's Waste bills",
                "Type 'SMITH' → all SMITH-123 records",
                "Type '174' → any bill/receipt/income around $174",
                "Type 'BPAY' → anything with a BPAY reference",
              ]}
            />
          </Section>
        </Chapter>

        {/* CH 19 — Series */}
        <Chapter num="19" title="Series & recurring bills" testid="ch-19">
          <p>
            Anything that repeats — Nolans instalments, Optus, insurance,
            subscriptions — should be a series so I generate future entries
            automatically.
          </p>
          <Section title="Creating a series">
            <Steps
              items={[
                "Add a bill as usual.",
                "In the detail page, expand 'Make this recurring'.",
                "Pick frequency (weekly / fortnightly / monthly / quarterly).",
                "How many future instalments to create (or 'ongoing').",
                "Save. All future bills are created in one hit.",
              ]}
            />
          </Section>
          <Section title="Editing a series">
            <p>
              Any bill that&apos;s part of a series shows a{" "}
              <span className="font-bold">purple series panel</span>. From
              there:
            </p>
            <Bullets
              items={[
                "Edit whole series — change amount, frequency, day on ALL future bills",
                "Cancel unpaid future — deletes future bills that haven't been paid, keeps history",
                "Extend series — add more future instalments",
              ]}
            />
          </Section>
        </Chapter>

        {/* CH 20 — Pay-links */}
        <Chapter num="20" title="Public pay-links" testid="ch-20">
          <p>
            Every bill has a public{" "}
            <span className="mono">/pay/&#123;id&#125;</span> URL that anyone
            with the link can open — no login required. Customers see the
            invoice, tap &quot;Mark as paid&quot;, and I update the record
            immediately.
          </p>
          <Section title="How to use">
            <Steps
              items={[
                "Open the bill's detail page.",
                "Click 'Copy pay-link'.",
                "Paste into an SMS or email to the customer.",
                "When they tap 'paid', I auto-mark it received and notify you.",
              ]}
            />
          </Section>
          <Callout label="Trust but verify" tone="warn">
            The pay-link is a self-service tool. Always check your bank
            account shows the funds before considering it truly paid. If
            someone marks paid but no money lands, contact them.
          </Callout>
        </Chapter>

        {/* CH 21 — Bank statement */}
        <Chapter num="21" title="Bank statement OCR" testid="ch-21">
          <p>
            Ingest a whole month&apos;s bank statement in one hit. Massive
            time-saver.
          </p>
          <Section title="How to use">
            <Steps
              items={[
                "SNAP → Upload → your bank statement PDF.",
                "I detect it's a BANK STATEMENT and OCR every line.",
                "For each transaction, I ask: Allocate to existing bill · Save as new bill · Save as receipt · Skip.",
                "Work through the list top-to-bottom (usually 15-20 min for a month).",
                "Save. You've now got every transaction categorised and matched.",
              ]}
            />
          </Section>
          <BillySays>
            Best done at month-end with a cuppa. Bang out 3 months at once
            and you&apos;re ready for BAS in one session.
          </BillySays>
        </Chapter>

        {/* ============== PART VI ============== */}
        <div className="break-before-page">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-1">
            Part VI
          </div>
          <h1 className="font-display font-black text-5xl leading-none mb-10">
            Reference
          </h1>
        </div>

        {/* CH 22 — FAQ */}
        <Chapter
          num="22"
          title="Frequently Asked Questions"
          subtitle="Real questions I get asked. If yours isn't here, ring your agent."
          testid="ch-22"
        >
          <Section title="General">
            <QA
              q="Do I need internet to use Billy?"
              a="Yes — Billy is a web app. All your data lives in a secure MongoDB database in the cloud. Offline mode isn't supported (yet)."
            />
            <QA
              q="Is my data safe?"
              a="Yes. Every action is logged (History tab), the database is backed up nightly by Emergent, and access requires your password. Nothing gets shared without your say-so."
            />
            <QA
              q="Can I use Billy on my phone?"
              a="Absolutely — it's mobile-first. Add it to your home screen for a one-tap app icon. iPhone: Safari → Share → Add to Home Screen. Android: Chrome → menu → Add to Home Screen."
            />
            <QA
              q="Can multiple people use Billy?"
              a="Right now it's a single-user login. If you need a second staff account, ask your agent — separate logins can be created with their own audit trail."
            />
          </Section>

          <Section title="SNAP & OCR">
            <QA
              q="What if SNAP guesses the wrong document type?"
              a="Tap the correct type at the top of the review page (BILL / RECEIPT / QUOTE / STATEMENT). The AI's about 95% right but not perfect — trust your own eyes on this one."
            />
            <QA
              q="Can I upload a PDF instead of a photo?"
              a="Yes. SNAP → Upload → pick the PDF. Works especially well for supplier invoices emailed to you and bank statements."
            />
            <QA
              q="How accurate is the amount extraction?"
              a="Extremely accurate for printed documents. Handwritten scrawl is trickier — always double-check the amount before hitting save."
            />
            <QA
              q="It's not reading my receipt properly."
              a="9 times out of 10 it's the photo. Retake with better light, no shadow, full doc in frame, portrait orientation. If still bad, edit the fields manually — it won't take long."
            />
          </Section>

          <Section title="Bills & payments">
            <QA
              q="I paid the wrong bill by accident. How do I undo?"
              a="Open the bill → payment history → tap the incorrect payment → 'Reverse'. This restores the balance and keeps a log of the reversal in History."
            />
            <QA
              q="Can I split one payment across multiple bills?"
              a="Not automatically. Record it as separate payments against each bill (e.g. $500 to bill A, $300 to bill B). Same result, cleaner audit trail."
            />
            <QA
              q="A supplier says I didn't pay but I did. Proof?"
              a="Open the bill → payment history shows the date and amount. Screenshot and email it to them. Argument over."
            />
          </Section>

          <Section title="Income & Allocate">
            <QA
              q="I marked income received but Allocate hasn't updated."
              a="Refresh the Allocate tab. If still not updating, check the income entry — sometimes the amount field is blank. The math needs a number."
            />
            <QA
              q="Can I change the priority order of buckets?"
              a="Yes — Allocate tab → 'Edit buckets' → drag to reorder. Wages and Super should always stay near the top."
            />
            <QA
              q="What if I bring in more than the buckets need?"
              a="The extra flows into the Buffer / Profit bucket. That's your reward for a good week — keep it separate so you don't spend it on impulse."
            />
            <QA
              q="What if I bring in less than the buckets need?"
              a="Lower-priority buckets get part-funded. Billy Insight tells you exactly what's short and which overdue receivables to chase to fix it."
            />
          </Section>

          <Section title="BAS & tax">
            <QA
              q="When do I do BAS?"
              a="Quarterly for most tradies: 28 Oct (Jul-Sep), 28 Feb (Oct-Dec), 28 Apr (Jan-Mar), 28 Jul (Apr-Jun). Print your Receipts BAS export the week before each deadline."
            />
            <QA
              q="Does Billy submit BAS for me?"
              a="No. Billy prepares the pack (GST paid, GST collected) — you or your accountant submits via the ATO portal or MyGov."
            />
            <QA
              q="Do I need Xero / MYOB as well?"
              a="Depends on your accountant. Billy handles day-to-day cashflow and BAS prep. If your accountant runs Xero, feed them Billy's CSV exports quarterly."
            />
          </Section>

          <Section title="Pay-links & customers">
            <QA
              q="Is the pay-link secure?"
              a="Anyone with the link can view the invoice details. Treat the link like a physical invoice — send it to the intended customer only."
            />
            <QA
              q="Do customers need to sign up?"
              a="No. They just tap the link, see the invoice, and tap 'Mark as paid'. Zero friction. That's the point."
            />
            <QA
              q="How do I chase multiple overdue customers at once?"
              a="Weekly tab → Overdue tile → open each → copy pay-link → SMS. Or Allocate tab → Chaseable panel lists them all in one spot."
            />
          </Section>
        </Chapter>

        {/* CH 23 — Troubleshooting */}
        <Chapter
          num="23"
          title="Troubleshooting"
          subtitle="When something breaks — start here."
          testid="ch-23"
        >
          <Section title="App won't load / white screen">
            <Bullets
              items={[
                "Hard refresh: Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac)",
                "On phone: close browser fully and reopen",
                "Still broken? Clear site data (browser settings)",
                "If it's happened right after a big update, wait 60 seconds and retry — the servers might be catching up",
              ]}
            />
          </Section>

          <Section title="Login not working">
            <Bullets
              items={[
                "Double-check email spelling — one word, ends in .com",
                "Password is Holden355 — capital H, rest lowercase, no spaces",
                "Try incognito to rule out cached bad state",
                "If still failing, ring your agent — they can reset from the admin side",
              ]}
            />
          </Section>

          <Section title="SNAP isn't working">
            <Bullets
              items={[
                "Camera permission blocked? Check your browser settings → site permissions → allow camera",
                "Upload failing? File too big — most bills should be under 10MB. Take a lower-res photo.",
                "Processing stuck? Reload the SNAP page and try again. Nothing gets lost.",
                "OCR results garbage? Retake with better lighting.",
              ]}
            />
          </Section>

          <Section title="A number looks wrong">
            <Bullets
              items={[
                "Check the History tab — every change is logged with who did what and when.",
                "Find the source record → open → verify the underlying data.",
                "If the record itself is wrong, edit it directly. History keeps the audit trail.",
                "Don't panic-delete — deleting loses context.",
              ]}
            />
          </Section>

          <Section title="Bill / income disappeared">
            <Bullets
              items={[
                "Check filters at the top — you might have accidentally filtered it out.",
                "Search Cmd+K for the vendor / client name.",
                "Check History tab for a delete action — you can find the ID and re-create if needed.",
              ]}
            />
          </Section>

          <Section title="Allocate numbers don't add up">
            <Bullets
              items={[
                "Make sure all Income for the week is marked Received (not just Expected).",
                "Check Overheads — did you mark rent/wages paid?",
                "Refresh the Allocate tab.",
                "Still off? Screenshot the discrepancy and send to your agent.",
              ]}
            />
          </Section>

          <Callout label="If you're stuck" tone="info">
            Screenshot the screen + describe what you did and what happened.
            Send both to your agent. Half the &quot;bugs&quot; are actually
            small misunderstandings that a 2-min chat fixes.
          </Callout>
        </Chapter>

        {/* CH 24 — Glossary */}
        <Chapter
          num="24"
          title="Glossary"
          subtitle="Every Billy term in plain English."
          testid="ch-24"
        >
          <div className="space-y-3 text-[14px]">
            <div><span className="font-display font-extrabold">Allocate.</span> The envelope-budgeting tab that splits received cash into wages, super, GST, rent, buffer — in priority order.</div>
            <div><span className="font-display font-extrabold">BAS.</span> Business Activity Statement. Quarterly ATO paperwork showing GST paid vs GST collected.</div>
            <div><span className="font-display font-extrabold">Bill.</span> An invoice you have received from a supplier. Money going OUT.</div>
            <div><span className="font-display font-extrabold">Bucket.</span> A category in Allocate — e.g. Wages, Super, Rent. Each bucket has a priority and a target amount.</div>
            <div><span className="font-display font-extrabold">Chase.</span> Sending a nudge (SMS or email with pay-link) to an overdue customer.</div>
            <div><span className="font-display font-extrabold">Expected income.</span> Income you know is coming but hasn&apos;t landed in the bank yet.</div>
            <div><span className="font-display font-extrabold">Forecast (3-Mo).</span> Projected bank balance over the next 12 weeks based on booked activity.</div>
            <div><span className="font-display font-extrabold">GST.</span> Goods and Services Tax — 10% on most Australian goods. Collected on your invoices, paid on your expenses. Net difference is remitted quarterly.</div>
            <div><span className="font-display font-extrabold">Income.</span> Money coming IN — deposits, service calls, factory repairs, etc.</div>
            <div><span className="font-display font-extrabold">Job tag.</span> A short ID (like SMITH-123) attached to bills, receipts, income so the Jobs tab can group them.</div>
            <div><span className="font-display font-extrabold">OCR.</span> Optical Character Recognition — how the AI reads text off a photo.</div>
            <div><span className="font-display font-extrabold">Overhead.</span> A fixed recurring cost — rent, wages, insurance. Same amount every week/month.</div>
            <div><span className="font-display font-extrabold">Pay-link.</span> A public URL customers can tap to view an invoice and mark it paid.</div>
            <div><span className="font-display font-extrabold">Receipt.</span> A record of a small expense you paid on the spot (petrol, Bunnings, cafe).</div>
            <div><span className="font-display font-extrabold">Received.</span> Status when money has actually hit your bank account (as opposed to Expected).</div>
            <div><span className="font-display font-extrabold">Series.</span> A group of recurring bills — payment plans, subscriptions — managed as one.</div>
            <div><span className="font-display font-extrabold">SNAP.</span> The black camera button on every page. The main way to add anything to Billy.</div>
            <div><span className="font-display font-extrabold">Super.</span> Superannuation — 11.5% of wages, legally required to be paid quarterly to your staff&apos;s super fund.</div>
          </div>
        </Chapter>

        {/* CH 25 — Rhythm */}
        <Chapter
          num="25"
          title="Your yearly rhythm"
          subtitle="A schedule that works. Print this, stick it on the workshop wall."
          testid="ch-25"
        >
          <Section title="Every morning (2 min)">
            <Bullets
              items={[
                "Open Weekly tab. Glance at what's due today.",
                "SNAP any bills / receipts from yesterday.",
              ]}
            />
          </Section>

          <Section title="When cash lands (30 sec)">
            <Bullets
              items={[
                "Income → tick the payment as received.",
                "Allocate → follow the split, actually move money to each account.",
              ]}
            />
          </Section>

          <Section title="Every Friday (10 min)">
            <Bullets
              items={[
                "Weekly → review the stacked chart.",
                "Print the weekly report.",
                "SMS pay-links to any overdue customers.",
                "Check Overheads → mark this week's rent / wages paid.",
              ]}
            />
          </Section>

          <Section title="End of month (15 min)">
            <Bullets
              items={[
                "Print Monthly Statement for records.",
                "SNAP anything missed during the month.",
                "SNAP the bank statement PDF and walk through each line.",
                "Grow tab → pick one revenue play to try next month.",
              ]}
            />
          </Section>

          <Section title="Every quarter (30 min)">
            <Bullets
              items={[
                "Receipts → Export BAS PDF + CSV.",
                "Print P&L for the quarter.",
                "Review the Jobs tab — which jobs actually made money.",
                "Email everything to your accountant.",
                "Book in super payment to staff fund (11.5% of quarterly wages).",
              ]}
            />
          </Section>

          <Section title="End of FY (1 hour)">
            <Bullets
              items={[
                "Print full-year P&L.",
                "Print full-year BAS pack.",
                "Print 3-Month Forecast for the coming year.",
                "Review Grow ideas — what worked, what didn't.",
                "Email everything to your accountant.",
                "Take a beer. You made it.",
              ]}
            />
          </Section>

          <BillySays>
            Habits &gt; effort. 5 minutes a day beats a 5-hour catch-up at
            month end every single time.
          </BillySays>
        </Chapter>

        {/* CLOSING PAGE */}
        <section
          className="border-t-4 border-black pt-8 mt-16 text-center break-before-page"
          data-testid="manual-close"
        >
          <BillyMark className="h-12 w-12 mx-auto" />
          <h2 className="font-display font-black text-4xl mt-6">
            SNAP. ALLOCATE. CHASE.
          </h2>
          <p className="text-neutral-600 mt-4 max-w-md mx-auto">
            Three actions, every day. Do them and I run the paperwork so you
            can run the business.
          </p>
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-neutral-500">
            <ArrowRight className="h-3.5 w-3.5" />
            <span>Bookmark this page. Print it. Keep it on the ute.</span>
          </div>
          <div className="mt-12 text-[10px] uppercase tracking-widest font-bold text-neutral-500">
            Billy Custom · The Outdoor Blind Company
            <br />
            Training Manual · Edition 2 · Printed {today}
          </div>
        </section>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 18mm 15mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:min-h-screen { min-height: 100vh !important; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
          .break-before-page { break-before: page; page-break-before: always; }
          .break-after-page { break-after: page; page-break-after: always; }
          h1, h2, h3 { break-after: avoid; page-break-after: avoid; }
        }
      `}</style>
    </div>
  );
};

export default Manual;
